import { CircularProgress, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { lazy, Suspense } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';

import CognitoLogin from '@uptime/shared/components/Login/CognitoLogin';
import { Mode } from '@uptime/shared/components/Login/CognitoLogin.types';
import { COGNITO_ENV_ADMIN } from '@uptime/shared/constants/cognito';

import PrivateRoute from './common/components/PrivateRoute';
import { Routes } from './constants/router';
import Layout from './pages/Layout';
import { setToken } from './store/actions/auth';
import './App.css';

const Users = lazy(() => import('./pages/Users'));
const UserPage = lazy(() => import('./pages/Users/partials/UserPage'));
const ChangePlanPage = lazy(() => import('./pages/Users/partials/ChangePlan'));
const Facilities = lazy(() => import('./pages/Facilities'));
const FacilitiesPage = lazy(() => import('./pages/Facilities/partials/FacilitiesPage'));
const Devices = lazy(() => import('./pages/Devices'));
const DeviceView = lazy(() => import('./containers/Devices/DeviceViewContainer'));
const DeviceUpdate = lazy(() => import('./containers/Devices/DeviceUpdateContainer'));
const WorkOrderAreas = lazy(() => import('./pages/WorkOrders/partials/WorkOrderAreas'));
const WorkOrderDevices = lazy(() => import('./pages/WorkOrders/partials/WorkOrderDevices'));
const BulkReportRepair = lazy(() => import('./pages/WorkOrders/BulkReportRepair'));
const DashboardAnalyticsView = lazy(() => import('./pages/Analytics'));
const CreateAdmin = lazy(() => import('./pages/Users/partials/CreateAdmin'));
const CreateTopUser = lazy(() => import('./pages/Users/partials/CreateTopUser'));
const CreateTeam = lazy(() => import('./pages/Users/partials/CreateTeam'));
const CreateTeamSet = lazy(() => import('./pages/Users/partials/TeamSet/CreateTeamSet'));
const EditTeam = lazy(() => import('./pages/Users/partials/EditTeam'));
const EditTeamSet = lazy(() => import('./pages/Users/partials/TeamSet/EditTeamSet'));
const Logs = lazy(() => import('./pages/Logs'));
const LogTypes = lazy(() => import('./pages/LogTypes'));
const Session = lazy(() => import('./pages/Devices/Sessions/Session'));
const SessionDevices = lazy(() => import('./pages/Devices/Sessions/SessionDevices'));
const TasksBulkCreation = lazy(() => import('./pages/Tasks/BulkCreation'));

function App() {
  return (
    <Switch>
      <Route path={`/${Mode.FederatedSignIn}`}>
        <CognitoLogin mode={Mode.FederatedSignIn} />
      </Route>
      <Route path={`/${Mode.FederatedSignOut}`} exact>
        <CognitoLogin mode={Mode.FederatedSignOut} />
      </Route>
      <Route path={`/${Mode.Callback}`} exact>
        <CognitoLogin mode={Mode.Callback} setToken={setToken} cognitoEnv={COGNITO_ENV_ADMIN} />
      </Route>
      <Route path={`/${Mode.Activation}`} exact>
        <CognitoLogin
          mode={Mode.Activation}
          setToken={setToken}
          cognitoEnv={COGNITO_ENV_ADMIN}
          welcomeText="UptimeAdmin"
        />
      </Route>
      <Layout>
        <Suspense
          fallback={
            <Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
              <CircularProgress size={16} color="primary" />
            </Box>
          }
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Switch>
              <PrivateRoute path={Routes.TeamSetView} component={EditTeamSet} />
              <PrivateRoute path={Routes.TeamView} component={EditTeam} />
              <PrivateRoute path={Routes.UserView} component={UserPage} />
              <PrivateRoute path={Routes.UserChangePlan} component={ChangePlanPage} />
              <PrivateRoute path={Routes.CreateAdmin} component={CreateAdmin} />
              <PrivateRoute path={Routes.CreateTopUser} component={CreateTopUser} />
              <PrivateRoute path={Routes.CreateTeam} component={CreateTeam} />
              <PrivateRoute path={Routes.CreateTeamSet} component={CreateTeamSet} />
              <PrivateRoute path={Routes.Users} component={Users} />
              <PrivateRoute path={Routes.FacilityView} component={FacilitiesPage} />
              <PrivateRoute path={Routes.Facilities} component={Facilities} />
              <PrivateRoute path={Routes.DeviceView} component={DeviceView} />
              <PrivateRoute path={Routes.DeviceUpdate} component={DeviceUpdate} />
              <PrivateRoute path={Routes.DeviceSessionCreate} exact component={Session} />
              <PrivateRoute path={Routes.DeviceSession} exact component={Session} />
              <PrivateRoute path={Routes.DeviceSessionDevices} exact component={SessionDevices} />
              <PrivateRoute path={Routes.Devices} component={Devices} />
              <PrivateRoute path={Routes.TasksBulkCreation} component={TasksBulkCreation} />
              <PrivateRoute path={Routes.WorkOrderAreas} component={WorkOrderAreas} />
              <PrivateRoute path={Routes.BulkReportRepair} component={BulkReportRepair} />
              <PrivateRoute path={Routes.WorkOrders} component={WorkOrderDevices} />
              <PrivateRoute path={Routes.LogTypes} component={LogTypes} />
              <PrivateRoute path={Routes.LogsCompleted} component={Logs} />
              <PrivateRoute path={Routes.Analytics} component={DashboardAnalyticsView} />
              <Redirect to={Routes.Users} />
            </Switch>
          </LocalizationProvider>
        </Suspense>
      </Layout>
    </Switch>
  );
}

export default App;
